import { Avatar, AvatarFallback, AvatarImage } from '@crowdfpl/ui-web/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@crowdfpl/ui-web/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@crowdfpl/ui-web/sheet';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@crowdfpl/ui-web/tabs';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { dialogContentResponsive } from '@/helpers/dialog-responsive';
import { cn } from '@/helpers/utils';
import { useInfiniteLeagueDuelPicksTab } from '@/hooks/data/leagues';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import type { LeagueDuelPick } from '@/types/leagues';

export type LeagueDuelVoteBreakdownProps = {
  leagueId: string;
  leagueName: string;
  duelSlug: string;
  player1Name: string;
  player2Name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function memberInitials(username: string): string {
  const t = username.trim();
  if (t.length >= 2) return t.slice(0, 2).toUpperCase();
  if (t.length === 1) return `${t.toUpperCase()}—`;
  return '—';
}

function MemberRow({ pick }: { pick: LeagueDuelPick }) {
  const initials = memberInitials(pick.username);
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border border-transparent px-2.5 py-2.5',
        pick.is_me
          ? 'border-l-4 border-l-primary bg-gradient-to-r from-primary/[0.07] to-primary/[0.02]'
          : 'bg-muted/50',
      )}
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={pick.avatar_url ?? undefined} alt="" />
        <AvatarFallback className="text-[10px] font-medium">{initials}</AvatarFallback>
      </Avatar>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm',
          pick.is_me ? 'font-semibold text-foreground' : 'font-medium text-foreground/85',
        )}
      >
        {pick.username}
      </span>
      {pick.is_me ? (
        <span className="shrink-0 text-sm leading-none text-primary" aria-hidden="true">
          ✓
        </span>
      ) : null}
    </div>
  );
}

type PicksTabQuery = ReturnType<typeof useInfiniteLeagueDuelPicksTab>;

function LeaguePicksVoterList({
  picks,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  error,
  emptyText,
  isMobile,
}: {
  picks: LeagueDuelPick[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
  emptyText: string;
  isMobile: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const loadMoreEl = loadMoreRef.current;
    if (!container || !loadMoreEl || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: container, threshold: 0.1, rootMargin: '50px' },
    );

    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const listMaxHeight = isMobile ? 'max-h-[min(40vh,320px)]' : 'max-h-[min(50vh,400px)]';

  const LoadingRows = () => (
    <div className="space-y-2">
      {Array.from({ length: isMobile ? 5 : 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return <LoadingRows />;
  }

  if (error) {
    return <div className="py-8 text-center text-sm text-destructive">{error.message}</div>;
  }

  if (picks.length === 0) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{emptyText}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        listMaxHeight,
        'space-y-2 overflow-y-auto pr-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20',
      )}
    >
      {picks.map(p => (
        <MemberRow key={p.user_id} pick={p} />
      ))}
      {hasNextPage ? (
        <div ref={loadMoreRef} className="flex justify-center py-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Loading…</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LeagueDuelVoteBreakdown({
  leagueId,
  leagueName,
  duelSlug,
  player1Name,
  player2Name,
  open,
  onOpenChange,
}: LeagueDuelVoteBreakdownProps) {
  const isMobile = useIsMobile();
  const picksA = useInfiniteLeagueDuelPicksTab({ leagueId, duelSlug, pick: 'player_a' }, { enabled: open });
  const picksB = useInfiniteLeagueDuelPicksTab({ leagueId, duelSlug, pick: 'player_b' }, { enabled: open });
  const picksNv = useInfiniteLeagueDuelPicksTab({ leagueId, duelSlug, pick: 'not_voted' }, { enabled: open });

  const breakdownLoading = picksA.isLoading || picksB.isLoading || picksNv.isLoading;
  const breakdownError = picksA.error ?? picksB.error ?? picksNv.error;

  const totalVoted = picksA.totalCount + picksB.totalCount;
  const totalMembers = picksA.totalCount + picksB.totalCount + picksNv.totalCount;

  const headerTitle = `${leagueName} votes`;
  const headerSubtitle = breakdownLoading ? 'Loading…' : `${totalVoted}/${totalMembers} members voted`;

  const countsReady = !breakdownLoading;
  const defaultTab = picksA.totalCount >= picksB.totalCount ? 'player_a' : 'player_b';
  const tabsKey = `${leagueId}:${duelSlug}:${countsReady ? 'ready' : 'loading'}`;

  const tabTriggerClass =
    'h-9 min-h-9 w-full cursor-pointer rounded-lg px-1.5 py-0 text-center text-[11px] font-medium leading-tight text-muted-foreground shadow-none transition-all ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'data-[state=active]:bg-background data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-xs';

  const listProps = (q: PicksTabQuery, empty: string) => ({
    picks: q.picks,
    isLoading: q.isLoading,
    isFetchingNextPage: q.isFetchingNextPage,
    hasNextPage: q.hasNextPage,
    fetchNextPage: q.fetchNextPage,
    error: q.error,
    emptyText: empty,
    isMobile,
  });

  let tabBlock: ReactNode;
  if (breakdownLoading) {
    tabBlock = (
      <div className="space-y-2 py-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
    );
  } else if (breakdownError) {
    tabBlock = <p className="py-10 text-center text-sm text-destructive">{breakdownError.message}</p>;
  } else {
    tabBlock = (
      <Tabs key={tabsKey} defaultValue={defaultTab} className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-muted/80 p-1">
          <TabsTrigger value="player_a" className={tabTriggerClass}>
            <span className="line-clamp-2 break-words">
              {player1Name} ({picksA.totalCount})
            </span>
          </TabsTrigger>
          <TabsTrigger value="player_b" className={tabTriggerClass}>
            <span className="line-clamp-2 break-words">
              {player2Name} ({picksB.totalCount})
            </span>
          </TabsTrigger>
          <TabsTrigger value="nv" className={tabTriggerClass}>
            <span className="line-clamp-2">Not voted ({picksNv.totalCount})</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="player_a" className="mt-4 space-y-0 outline-none">
          <LeaguePicksVoterList {...listProps(picksA, `No votes for ${player1Name}`)} />
        </TabsContent>
        <TabsContent value="player_b" className="mt-4 space-y-0 outline-none">
          <LeaguePicksVoterList {...listProps(picksB, `No votes for ${player2Name}`)} />
        </TabsContent>
        <TabsContent value="nv" className="mt-4 space-y-0 outline-none">
          <LeaguePicksVoterList {...listProps(picksNv, 'Everyone voted')} />
        </TabsContent>
      </Tabs>
    );
  }

  const sheetCloseBtnClasses =
    '[&>button[type="button"]]:opacity-55 [&>button[type="button"]]:transition-opacity ' +
    '[&>button[type="button"]]:hover:opacity-90 [&>button[type="button"]]:right-3 [&>button[type="button"]]:top-3 ' +
    '[&>button[type="button"]]:p-1.5 [&>button[type="button"]>svg]:h-3.5 [&>button[type="button"]>svg]:w-3.5';

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className={cn(
            'flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-t-2xl p-0 px-4 pb-6 pt-5 sm:px-5',
            sheetCloseBtnClasses,
          )}
        >
          <SheetHeader className="mb-3 shrink-0 space-y-1.5 p-0 text-left">
            <SheetTitle className="pr-10 text-[15px] font-semibold leading-tight tracking-tight text-foreground">
              {headerTitle}
            </SheetTitle>
            <SheetDescription asChild>
              <p className="text-[12px] leading-snug text-muted-foreground">{headerSubtitle}</p>
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-hidden">{tabBlock}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          dialogContentResponsive,
          'flex max-h-[min(85vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px]',
        )}
      >
        <DialogHeader className="shrink-0 space-y-1.5 px-6 pb-0 pt-6 text-left">
          <DialogTitle className="pr-10 text-[15px] font-semibold leading-tight tracking-tight text-foreground">
            {headerTitle}
          </DialogTitle>
          <DialogDescription className="text-[12px] leading-snug text-muted-foreground">
            {headerSubtitle}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden px-6 pb-6 pt-3">{tabBlock}</div>
      </DialogContent>
    </Dialog>
  );
}
